import * as FryUtilsCommon from '@fry/lib/utils/common';

/**
 * Node which is received from the form rendering layers and the CouchDB.
 */
export interface TreeFormControlNode {
  /**
   * CouchDB ID of the node (tree option)
   */
  _id: string;

  /**
   * WTF ID which gets created by the form rendering layers
   */
  id?: string;

  /**
   * Name of the node, this is the text which is displayed
   * in the tree and provided in the Tree editor
   */
  name: string;

  /**
   * Child nodes of the node, it is called `categories` because
   * underlying form rendering layers are using this name.
   */
  categories: TreeFormControlNode[];
}

/**
 * Angular Material Tree Flat Node
 * 
 * This is what the Angular Material Tree component expects and works
 * with, we provide a conversion function to convert the `TreeFormControlNode`
 * into the `TreeFormControlFlatNode` and vice versa.
 */
export interface TreeFormControlFlatNode {
  id: string;
  expandable: boolean;
  name: string;
  level: number;
}

/**
 * Configuration options for the TreeSelectorFormControl and TreeEditorFormControl
 */
export interface TreeFormControlOptions {

  /**
   * Whether the tree should be displayed in a hierarchy or flat list.
   * 
   * This is valid for both Editor and Selector and is used to determine
   * if sub-options should be displayed (allowed to create).
   */
  allowsHierarchy: boolean;
}

export class TreeFormControl {

  /**
   * Helper to create an empty node with a generated ID 
   * 
   * @returns An empty node with a generated ID
   */
  public static emptyNode(): TreeFormControlNode {
    return { _id: FryUtilsCommon.guid(), name: '', categories: [] };
  }

  /**
   * Transform from the TreeFormControlNode to the TreeFormControlFlatNode
   * 
   * @param node Node from the CouchDB
   * @param level Level of the node in the tree
   * @returns Node for the MatTree
   */
  public static nodeToFlatNode(node: TreeFormControlNode, level: number): TreeFormControlFlatNode {
    return {
      expandable: !!node.categories && node.categories.length > 0,
      name: node.name,
      level: level,
      id: node._id ? node._id : node.id // WTF: CouchDB vs. Form rendering layers
    };
  }

  /**
   * Depth-first pre-order search for a node with the given ID
   * 
   * NOTE: These are naive implementations to get the job done.
   *       There may be better solutions.
   * 
   * @param id ID of the node to find
   * @param nodes Set of nodes to perform search on
   * @returns Node or undefined if not found
   */
  public static findNodeByID(id: string, nodes: TreeFormControlNode[]): TreeFormControlNode | undefined {
    for (const node of nodes) {
      if (node._id === id) { return node; }
      const result = this.findNodeByID(id, node.categories);
      if (result) { return result; }
    }
    return undefined;
  }

  /**
   * Depth-first pre-order search for a parent of a node with the given ID
   * 
   * NOTE: These are naive implementations to get the job done.
   *       There may be better solutions.
   * 
   * @param id ID of the node to find
   * @param parent Parent candidate of the node to find
   * @param nodes Set of nodes to perform search on
   * @returns Node or undefined if not found
   */
  public static findParentNodeForChildNodeWithID(id: string,
                                                 parent: TreeFormControlNode | undefined,
                                                 nodes: TreeFormControlNode[]): TreeFormControlNode | undefined {
    for (const node of nodes) {
      if (node._id === id) { return parent; }
      const result = this.findParentNodeForChildNodeWithID(id, node, node.categories);
      if (result) { return result; }
    }
    return undefined;
  }
}
